import type { ObjectDirective } from "vue";

/* ---------- Types ---------- */
type MaskToken = "#" | "A" | "a" | "x";

type MaskElement = HTMLElement & {
  __inputMaskHandler?: (event: Event) => void;
  __beforeInputHandler?: (event: InputEvent) => void;
  __isApplyingMask?: boolean;
  __lastCursor?: number;
  __isPaste?: boolean;
};

/* ---------- Helpers ---------- */
/*
 * Resolve the actual native input.
 * This allows the directive to work on either:
 * - a real <input>
 * - a wrapper element that contains an <input>
 */
const resolveInput = (el: HTMLElement): HTMLInputElement | null => {
  if (el instanceof HTMLInputElement) return el;
  return el.querySelector("input");
};

const isMaskToken = (char: string): char is MaskToken => {
  return char === "#" || char === "A" || char === "a" || char === "x";
};

const matchesToken = (token: MaskToken, value: string): boolean => {
  if (token === "#") return /\d/.test(value);
  if (token === "x") return /[a-z0-9]/i.test(value);
  return /[a-z]/i.test(value);
};

const isMaskTokenValue = (char: string): boolean => {
  return /[a-z0-9]/i.test(char);
};

const hasRemainingTokenValue = (source: string, startIndex: number): boolean => {
  for (let i = startIndex; i < source.length; i++) {
    if (isMaskTokenValue(source[i])) {
      return true;
    }
  }

  return false;
};

const setCapitalLetter = (rawValue: string, mask: string): string => {
  for (let i = 0; i < mask.length; i++) {
    const currentChar = rawValue.charAt(i);

    if (mask.charAt(i) === "A" && currentChar && currentChar.toLocaleLowerCase()) {
      rawValue = rawValue.slice(0, i) + rawValue[i].toUpperCase() + rawValue.slice(i + 1);
    }
  }
  return rawValue;
};
/*
 * Apply the mask to the current input value.
 *
 * If the user edits in the middle, we remove everything after
 * that edit point so the value rebuilds cleanly.
 */

const applyMask = (rawValue: string, mask: string, editIndex: number, isPaste: boolean): string => {
  if (!mask) return rawValue;

  let source = String(rawValue ?? "");

  if (mask.includes("A")) {
    source = setCapitalLetter(rawValue, mask);
  }

  const startIndex = editIndex === 0 ? 1 : 0;
  const endIndex = editIndex === 0 ? editIndex + 2 : editIndex + 1;

  /*
    if the mask start with a non-token like a "("
    we make sure that it added on
  */
  if (!isMaskToken(mask.charAt(0)) && editIndex < source.length && isPaste === false) {
    const tokenCheck = source.at(-2);

    if (tokenCheck && isMaskTokenValue(tokenCheck)) {
      source = source.slice(startIndex, endIndex);
    }
  } else if (editIndex < source.length && isPaste === false) {
    /* if it starts with a token */
    const tokenCheck = source.at(editIndex);
    if (editIndex === 0) {
      source = source.slice(0, 1);
    } else if (tokenCheck && isMaskTokenValue(tokenCheck)) {
      source = source.slice(startIndex, endIndex);
    }
  }

  let result = "";
  let sourceIndex = 0;

  for (let maskIndex = 0; maskIndex < mask.length; maskIndex++) {
    const maskChar = mask[maskIndex];
    /*
     * Only show mask literals after we actually have some input.
     * This prevents the empty value from rendering as "() -"
     */
    if (!isMaskToken(maskChar)) {
      /*
       * Show the opening parenthesis as soon as the user has typed
       * at least one character.
       */
        if (hasRemainingTokenValue(source, sourceIndex)) {
          result += maskChar;
        }

      continue;
    }

    /*
     * Find the next source character that matches this token.
     */
    let matched = false;

    while (sourceIndex < source.length) {
      const currentChar = source[sourceIndex++];

      if (matchesToken(maskChar, currentChar)) {
        result += currentChar;
        matched = true;
        break;
      }
    }

    /*
     * Stop building the mask when there is no more source input.
     */
    if (!matched) {
      break;
    }
  }

  return result;
};
const setMaskedValue = (maskEl: MaskElement, input: HTMLInputElement, mask: string, editIndex: number, isPaste: boolean) => {
  /*
   * Prevent the directive from responding to the synthetic input
   * event that it dispatches itself.
   */
  if (maskEl.__isApplyingMask) return;

  const maskedValue = applyMask(input.value, mask, editIndex, isPaste);

  if (input.value !== maskedValue) {
    maskEl.__isApplyingMask = true;
    input.value = maskedValue;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    maskEl.__isApplyingMask = false;
  }
};

/* ---------- Directive ---------- */
const vInputMask: ObjectDirective<HTMLElement, string> = {
  inserted(el, binding) {
    const input = resolveInput(el);
    if (!input) return;

    const mask = String(binding.value ?? "");
    if (!mask) return;

    const maskEl = el as MaskElement;

    /*
     * Capture the cursor before the browser and mask logic
     * move it around.
     */
    const onBeforeInput = (e: InputEvent) => {
      maskEl.__lastCursor = input.selectionStart ?? input.value.length;
      maskEl.__isPaste = e.inputType === "insertFromPaste";
    };

    const onInput = () => {
      if (maskEl.__isApplyingMask) return;

      const editIndex = maskEl.__lastCursor ?? input.value.length;
      const isPaste = !!maskEl.__isPaste;
      /*
       * Use the cursor captured before the input change.
       * This is much more reliable than reading selectionStart
       * after the input has already been reformatted.
       */

      setMaskedValue(maskEl, input, mask, editIndex, isPaste);
    };

    maskEl.__beforeInputHandler = onBeforeInput;
    maskEl.__inputMaskHandler = onInput;
    const isPaste = !!maskEl.__isPaste;

    input.addEventListener("beforeinput", onBeforeInput);
    input.addEventListener("input", onInput);
    /*
     * Initial pass for pre-filled values.
     */
    setMaskedValue(maskEl, input, mask, input.value.length, isPaste);
  },

  update(el, binding) {
    /*
     * Resolve the actual input element the directive is operating on.
     */
    const input = resolveInput(el);
    if (!input) return;

    /*
     * Normalize the incoming mask value from the directive binding.
     * If the mask is null or empty, there is nothing to apply.
     */
    const nextMask = String(binding.value ?? "");
    if (!nextMask) return;

    const maskEl = el as MaskElement;
    const isPaste = !!maskEl.__isPaste;
    /*
     * Reapply the mask using the current input value.
     * This allows the directive to respond when the mask pattern
     * itself changes dynamically.
     *
     * Example:
     * mask="###-AAA"  ->  mask="AAA-###"
     */
    setMaskedValue(maskEl, input, nextMask, input.value.length, isPaste);
  },

  unbind(el) {
    /*
        1. Resolve the correct input element
        2. Remove event listeners
        3. Clean up directive state
     */
    const input = resolveInput(el);
    if (!input) return;

    const maskEl = el as MaskElement;

    /*
     * Remove the beforeinput listener that captured the cursor
     * position before the browser modified the input value.
     */
    if (maskEl.__beforeInputHandler) {
      input.removeEventListener("beforeinput", maskEl.__beforeInputHandler);
    }

    /*
     * Remove the input listener responsible for applying the mask
     * and reformatting the value after each user edit.
     */
    if (maskEl.__inputMaskHandler) {
      input.removeEventListener("input", maskEl.__inputMaskHandler);
    }

    /*
     * Clean up directive state that was stored on the element.
     * This prevents memory leaks and ensures the element does not
     * retain mask-related metadata after the directive is destroyed.
     */
    delete maskEl.__beforeInputHandler;
    delete maskEl.__inputMaskHandler;
    delete maskEl.__isApplyingMask;
    delete maskEl.__lastCursor;
  delete maskEl.__isPaste;
  },
};

export default vInputMask;
